import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Switch,
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Chip,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import UpdateBankAccountDialog from "./UpdateBankAccountDialog";

export interface PayoutData {
  bankAccount: {
    holderName: string;
    bankName: string;
    accountLast4: string;
    routingLast4: string;
    type: string;
  };
  autoPayout: boolean;
  threshold: string;
  taxVerified: boolean;
}

interface PayoutSettingsSectionProps {
  data: PayoutData;
  onSave: (data: PayoutData) => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#178B8E" },
};

const THRESHOLD_OPTIONS = ["$25", "$50", "$100", "$200"];

const PayoutSettingsSection: React.FC<PayoutSettingsSectionProps> = ({ data, onSave }) => {
  const [draft, setDraft] = useState<PayoutData>(data);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);

  const handleBankSave = (bankData: {
    holderName: string;
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    type: string;
  }) => {
    const last4account = bankData.accountNumber.slice(-4);
    const last4routing = bankData.routingNumber.slice(-4);
    setDraft((prev) => ({
      ...prev,
      bankAccount: {
        holderName: bankData.holderName,
        bankName: bankData.bankName,
        accountLast4: last4account,
        routingLast4: last4routing,
        type: bankData.type,
      },
    }));
  };

  const handleManualPayout = () => {
    console.log("Manual payout requested");
  };

  const handleDownload1099 = () => {
    console.log("1099 download requested");
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Payout Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage how and when you receive your earnings
      </Typography>

      {/* Bank Account */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <AccountBalanceIcon sx={{ color: "#178B8E", fontSize: 20 }} />
        <Typography variant="body1" fontWeight={600}>
          Bank Account
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: "#F8F8F8",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Account holder</Typography>
            <Typography variant="body2" fontWeight={500}>{draft.bankAccount.holderName}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Bank</Typography>
            <Typography variant="body2" fontWeight={500}>{draft.bankAccount.bankName}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Account number</Typography>
            <Typography variant="body2" fontWeight={500} sx={{ letterSpacing: 1 }}>
              ••••{draft.bankAccount.accountLast4}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Routing number</Typography>
            <Typography variant="body2" fontWeight={500} sx={{ letterSpacing: 1 }}>
              ••••{draft.bankAccount.routingLast4}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Type</Typography>
            <Typography variant="body2" fontWeight={500}>{draft.bankAccount.type}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Button
        variant="outlined"
        size="small"
        onClick={() => setBankDialogOpen(true)}
        sx={{ textTransform: "none", borderColor: "#178B8E", color: "#178B8E", mb: 3 }}
      >
        Update Bank Account
      </Button>

      <Divider sx={{ mb: 3 }} />

      {/* Payout Schedule */}
      <Typography variant="body1" fontWeight={600} mb={2}>
        Payout Schedule
      </Typography>

      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="body2" fontWeight={500}>Auto-payout</Typography>
          <Typography variant="caption" color="text.secondary">
            Automatically transfer earnings when balance reaches threshold
          </Typography>
        </Box>
        <Switch
          checked={draft.autoPayout}
          onChange={() => setDraft((prev) => ({ ...prev, autoPayout: !prev.autoPayout }))}
          size="small"
          sx={switchSx}
        />
      </Box>

      {draft.autoPayout ? (
        <TextField
          select
          size="small"
          label="Payout threshold"
          value={draft.threshold}
          onChange={(e) => setDraft((prev) => ({ ...prev, threshold: e.target.value }))}
          sx={{ minWidth: 160 }}
        >
          {THRESHOLD_OPTIONS.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={handleManualPayout}
          sx={{ textTransform: "none", borderColor: "#178B8E", color: "#178B8E" }}
        >
          Request Manual Payout
        </Button>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Tax Information */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <ReceiptLongIcon sx={{ color: "#178B8E", fontSize: 20 }} />
        <Typography variant="body1" fontWeight={600}>
          Tax Information
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            label={draft.taxVerified ? "Verified" : "Not provided"}
            size="small"
            sx={{
              backgroundColor: draft.taxVerified ? "rgba(23,139,142,0.1)" : "#FFF3E0",
              color: draft.taxVerified ? "#178B8E" : "#E65100",
              fontWeight: 600,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {draft.taxVerified ? "W-9 on file" : "Required for annual earnings over $600"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: "none", borderColor: "#178B8E", color: "#178B8E" }}
          >
            Update Tax Info
          </Button>
          {draft.taxVerified && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload1099}
              sx={{ textTransform: "none", borderColor: "#178B8E", color: "#178B8E" }}
            >
              Download 1099
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onSave(draft)}
          sx={{ textTransform: "none", backgroundColor: "#178B8E", "&:hover": { backgroundColor: "#146f72" } }}
        >
          Save Changes
        </Button>
      </Box>

      <UpdateBankAccountDialog
        open={bankDialogOpen}
        onClose={() => setBankDialogOpen(false)}
        onSave={handleBankSave}
      />
    </Paper>
  );
};

export default PayoutSettingsSection;
